import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { toast } from 'sonner';
import { GraduationCap, PlayCircle, CheckCircle, Clock, Award } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TrainingPortalEmbed = ({ impersonatedUser, adminUser }) => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTrainingData();
  }, [impersonatedUser]);

  const fetchTrainingData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const response = await axios.get(
        `${API}/superadmin/impersonate/training/${impersonatedUser.employee_id}`,
        config
      );
      
      setAssignments(response.data.assignments || []);
    } catch (error) {
      console.error('Error fetching training data:', error);
      toast.error('Unable to fetch training data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (assignment) => {
    if (assignment.certificate_earned) {
      return <Badge className="bg-green-600"><Award className="w-3 h-3 mr-1" />Certified</Badge>;
    }
    if (assignment.assessment_completed) {
      return <Badge className="bg-blue-600"><CheckCircle className="w-3 h-3 mr-1" />Completed</Badge>;
    }
    if (assignment.video_completed) {
      return <Badge className="bg-purple-600"><PlayCircle className="w-3 h-3 mr-1" />Video Done</Badge>;
    }
    return <Badge className="bg-amber-600"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
  };

  const getProgress = (assignment) => {
    let progress = 0;
    if (assignment.video_completed) progress += 50;
    if (assignment.assessment_completed) progress += 30;
    if (assignment.certificate_earned) progress += 20;
    return progress;
  };

  // Stats calculation
  const stats = {
    total: assignments.length,
    completed: assignments.filter(a => a.certificate_earned).length,
    inProgress: assignments.filter(a => !a.certificate_earned && (a.video_completed || a.assessment_started)).length,
    pending: assignments.filter(a => !a.video_completed && !a.assessment_started).length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="training-portal-embed">
      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card className="bg-white shadow-sm">
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-600">{stats.total}</p>
              <p className="text-xs text-gray-500">Total Assigned</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white shadow-sm">
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
              <p className="text-xs text-gray-500">Certified</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white shadow-sm">
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600">{stats.inProgress}</p>
              <p className="text-xs text-gray-500">In Progress</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white shadow-sm">
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
              <p className="text-xs text-gray-500">Not Started</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Training List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <GraduationCap className="w-5 h-5 text-teal-600" />
            Assigned Trainings for {impersonatedUser.name}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {assignments.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <GraduationCap className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No trainings assigned</p>
            </div>
          ) : (
            <div className="space-y-4">
              {assignments.map(assignment => (
                <div 
                  key={assignment.id}
                  className={`p-4 border rounded-lg ${assignment.certificate_earned ? 'bg-green-50 border-green-200' : 'bg-white'}`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h4 className="font-semibold text-gray-900">{assignment.training_name}</h4>
                      <p className="text-sm text-gray-500">{assignment.training_category || 'General'}</p>
                    </div>
                    {getStatusBadge(assignment)}
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 text-sm mb-3">
                    <div>
                      <span className="text-gray-500">Assigned:</span>
                      <span className="ml-1 font-medium">{formatDate(assignment.assigned_at)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Deadline:</span>
                      <span className={`ml-1 font-medium ${new Date(assignment.deadline) < new Date() && !assignment.certificate_earned ? 'text-red-600' : ''}`}>
                        {formatDate(assignment.deadline)}
                      </span>
                    </div>
                    {assignment.assessment_score !== null && (
                      <div>
                        <span className="text-gray-500">Score:</span>
                        <span className="ml-1 font-bold text-blue-600">{assignment.assessment_score}%</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>Progress</span>
                      <span>{getProgress(assignment)}%</span>
                    </div>
                    <Progress value={getProgress(assignment)} className="h-2" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TrainingPortalEmbed;
